import ErrorOutlineSharpIcon from "@mui/icons-material/ErrorOutlineSharp";
import {
  Box,
  Card,
  CardContent,
  List,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo } from "react";

import { useModal } from "@ebay/nice-modal-react";
import useQueryStringState from "components/hooks/useQueryStringState";
import Pagination from "components/ui/atoms/Pagination";
import FilterAndSort from "components/ui/molecules/FilterAndSort";
import Layout from "components/ui/molecules/Layout";
import CommitteeFilterAndSort from "models/deals/CommitteeFilterAndSort";
import CommitteeList from "models/deals/CommitteeList";
import DealFilterForm from "models/deals/DealFilterForm";
import DealHeader from "models/deals/DealHeader";
import DealListItem from "models/deals/DealListItem";
import DealSortForm from "models/deals/DealSortForm";
import { useAllDeal } from "models/deals/apiDeal";
import SharingFilterAndSort from "models/sharings/SharingsFilterAndSort";

export const DISPLAY_MODE_SHARINGS = "SHARINGS";
export const DISPLAY_MODE_COMMITTEE = "COMMITTEE";

const Deal = () => {
  const theme = useTheme();
  const [state, setState] = useQueryStringState();
  const dealModal = useModal("DealDrawer");
  const dealSharingModal = useModal("DealSharingLinksDrawer");
  const {
    data: deals,
    isLoading: dealsIsLoading,
    isError: dealsIsError,
    isSuccess: dealsIsSuccess,
    refetch,
  } = useAllDeal({
    ...state.filter,
    ...state.filter_sharing,
    ...state.sort,
    offset: state.offset,
    search: state.search,
    display_mode: state.displayMode,
  });

  const setDisplayMode = (displayMode) => {
    setState((prevState) => ({
      ...prevState,
      displayMode: displayMode,
    }));
  };

  useEffect(() => {
    refetch();
  }, [state]);

  const dealsSkeleton = (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Skeleton variant="rectangular" height={150} />
      <Skeleton variant="rectangular" height={150} />
      <Skeleton variant="rectangular" height={150} />
    </Stack>
  );

  const dealIds = useMemo(() => {
    if (state.displayMode !== DISPLAY_MODE_COMMITTEE)
      return deals?.results?.length > 1
        ? deals.results.map((deal) => deal.id)
        : null;
    else {
      let ids = [];
      deals?.new_deals &&
        deals.new_deals.map((deal_list) => {
          deal_list.deals.map((deal) => ids.push(deal.id));
        });
      deals?.current_deals &&
        deals.current_deals.map((deal_list) => {
          deal_list.deals.map((deal) => ids.push(deal.id));
        });
      deals?.investment_deals &&
        deals.investment_deals.map((deal_list) => {
          deal_list.deals.map((deal) => ids.push(deal.id));
        });
      return ids;
    }
  }, [state.displayMode, deals]);

  if (dealsIsError) return <>Error loading deals</>;

  return (
    <Layout container hideBreadcrumbs>
      <Layout title variant="component">
        <DealHeader
          displayMode={state.displayMode}
          setDisplayMode={setDisplayMode}
        />
      </Layout>
      <Layout content>
        <FilterAndSort
          FilterForm={DealFilterForm}
          SortForm={DealSortForm}
          state={state}
          setState={setState}
          count={deals?.count}
        />
        {state.displayMode == DISPLAY_MODE_COMMITTEE && (
          <CommitteeFilterAndSort
            state={state.filter}
            setState={(new_state) =>
              setState({
                ...state,
                filter: new_state,
                offset: 0,
              })
            }
            onClose={() =>
              setState({
                ...state,
                displayMode: undefined,
                offset: 0,
              })
            }
            refetchList={refetch}
          />
        )}
        {state.displayMode == DISPLAY_MODE_SHARINGS && (
          <SharingFilterAndSort
            state={state.filter_sharing}
            setState={(filter_sharing) =>
              setState({
                ...state,
                filter_sharing: filter_sharing,
                offset: 0,
              })
            }
            onClose={() =>
              setState({
                ...state,
                filter_sharing: undefined,
                displayMode: undefined,
                offset: 0,
              })
            }
          />
        )}
      </Layout>
      <Layout content>
        {dealsIsLoading ? (
          dealsSkeleton
        ) : state.displayMode == DISPLAY_MODE_COMMITTEE ? (
          deals.count > 500 ? (
            <Card
              sx={{
                backgroundColor: `${theme.palette.warning.light}22`,
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center" }}>
                <ErrorOutlineSharpIcon sx={{ mr: 2 }} />
                <Typography variant="h6" textAlign="center">
                  Please select more filters{" "}
                  <Typography variant="caption">{`(max results < 500)`}</Typography>
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <CommitteeList
              committeeDealsList={deals}
              onClick={(dealId) =>
                dealModal.show({
                  id: dealId,
                  formType: "update",
                  values: dealIds,
                })
              }
            />
          )
        ) : (
          <>
            <List variant="boxed">
              {deals.results.map((deal, index) => (
                <DealListItem
                  key={index}
                  object={deal}
                  onClick={(dealId) => {
                    if (state.displayMode == DISPLAY_MODE_SHARINGS) {
                      dealSharingModal.show({
                        id: dealId,
                        formType: "update",
                        values: dealIds,
                      });
                    } else {
                      dealModal.show({
                        id: dealId,
                        formType: "update",
                        values: dealIds,
                      });
                    }
                  }}
                  displayMode={state.displayMode}
                />
              ))}
            </List>
            <Box alignItems="center" justifyContent="center" mt={4}>
              <Pagination
                offset={state.offset || 0}
                setOffset={(offset) => {
                  setState({
                    ...state,
                    offset: offset,
                  });
                }}
                count={deals.count}
              />
            </Box>
          </>
        )}
      </Layout>
    </Layout>
  );
};

const sorts = [
  ["Newer added", "-created_at"],
  ["Older added", "created_at"],
  ["Newer updated", "-updated_at"],
  ["Older updated", "updated_at"],
  ["Step ascending", "investment_status"],
  ["Step descending", "-investment_status"],
];

export default Deal;
